import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { searchPatients } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import type { DoctorListItem } from "../../services/doctor.service";
import { QueueAPI } from "../../services/queue.service";
import type { QueuePriorityTag } from "../../services/queue.service";
import { AddPatientModal } from "../patients/AddPatientModal";

interface Props {
  isOpen: boolean;
  doctors: DoctorListItem[];
  onClose: () => void;
  onRegistered: () => void;
}

type SelectedPatient = { patient_id: string; name: string; contact_number?: string };

export const RegisterWalkinModal: React.FC<Props> = ({ isOpen, doctors, onClose, onRegistered }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PatientDbRecord[]>([]);
  const [searching, setSearching] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [selected, setSelected] = useState<SelectedPatient | null>(null);
  const [doctorId, setDoctorId] = useState("");
  const [priorityTag, setPriorityTag] = useState<QueuePriorityTag>("none");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setQuery(""); setResults([]); setSelected(null);
    setDoctorId(""); setPriorityTag("none");
  };

  // Same debounced-search pattern as AddMedicineModal's drug search.
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await searchPatients(query.trim());
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleRegister = async () => {
    if (!selected || !doctorId) {
      toast.error("Select a patient and a doctor");
      return;
    }
    setSubmitting(true);
    try {
      const { entry, beyond_hours_warning } = await QueueAPI.register({
        patient_id: selected.patient_id,
        doctor_id: doctorId,
        priority_tag: priorityTag,
      });
      toast.success(
        beyond_hours_warning
          ? `Added to queue (position ${entry.position}) - may not be seen today at current pace`
          : `Added to queue - position ${entry.position}`,
      );
      reset();
      onRegistered();
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to register walk-in");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800">Register Walk-in</h2>
            <button onClick={() => { reset(); onClose(); }} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {!selected ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Find Patient</label>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Name, phone, or PAT-xxxx"
                    autoFocus
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </div>

                {searching && <p className="text-xs text-slate-400">Searching…</p>}

                {results.length > 0 && (
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100">
                    {results.map((p) => (
                      <button
                        key={p.patient_id}
                        onClick={() => setSelected({
                          patient_id: p.patient_id!,
                          name: `${p.first_name} ${p.last_name}`,
                          contact_number: p.contact_number,
                        })}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
                      >
                        <span className="text-sm text-slate-700">{p.first_name} {p.last_name}</span>
                        <span className="text-xs text-slate-400">{p.contact_number}</span>
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setShowAddPatient(true)}
                  className="w-full border border-dashed border-cyan-300 text-cyan-600 py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-50"
                >
                  + New Patient
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-100">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{selected.name}</div>
                    {selected.contact_number && (
                      <div className="text-xs text-slate-500">{selected.contact_number}</div>
                    )}
                  </div>
                  <button onClick={() => setSelected(null)} className="text-xs text-cyan-700 hover:underline">Change</button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Doctor *</label>
                  <select
                    value={doctorId}
                    onChange={(e) => setDoctorId(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  >
                    <option value="">Select a doctor</option>
                    {doctors.map((d) => (
                      <option key={d.doctor_id} value={d.doctor_id}>{d.name} — {d.specialization}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Priority (visual only)</label>
                  <select
                    value={priorityTag}
                    onChange={(e) => setPriorityTag(e.target.value as QueuePriorityTag)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  >
                    <option value="none">None</option>
                    <option value="senior_citizen">Senior Citizen</option>
                    <option value="emergency">Emergency</option>
                    <option value="staff_family">Staff Family</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => { reset(); onClose(); }}
                    className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRegister}
                    disabled={submitting || !doctorId}
                    className="flex-1 bg-cyan-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-600 disabled:opacity-40"
                  >
                    {submitting ? "Adding…" : "Add to Queue"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Same modal as the Patients page, so both paths share one form and validation. */}
      <AddPatientModal
        isOpen={showAddPatient}
        onClose={() => setShowAddPatient(false)}
        onSuccess={(p) => {
          // Only a server-created patient can join the queue; one saved offline
          // (no p) shows up in search once it syncs.
          if (p?.patient_id) {
            setSelected({ patient_id: p.patient_id, name: `${p.first_name} ${p.last_name}`, contact_number: p.contact_number });
          }
        }}
      />
    </>
  );
};
